/**
 * reportRenderer.ts
 *
 * Pure-function utilities for executing AdminReportSectionDto scripts and
 * producing HTML output strings.  These functions have no React dependency and
 * can be used from any component (ReportView, WellVisitView inline display,
 * PDF export, etc.).
 */

import { AdminResponseDto } from '../types/AdminResponseDto';
import { AdminReportSectionDto, AdminAssessmentReportDto } from '../types/adminAssessment';
import { AppProfilePHIDto } from '../types/api/appProfile';

// ── Section helpers ──────────────────────────────────────────────────────────

/**
 * Collect all AdminReportSectionDto objects from every subReport within a
 * report, in order.
 */
export const getAllReportSections = (
  report: AdminAssessmentReportDto
): AdminReportSectionDto[] => {
  const sections: AdminReportSectionDto[] = [];
  report.subReports?.forEach(subReport => {
    subReport.reportSections?.forEach(section => sections.push(section));
  });
  return sections;
};

/**
 * Collect sections retaining the parent subReport name for display purposes.
 */
export const getAllReportSectionsWithSubReport = (
  report: AdminAssessmentReportDto
): { subReportName: string; section: AdminReportSectionDto }[] => {
  const result: { subReportName: string; section: AdminReportSectionDto }[] = [];
  report.subReports?.forEach(subReport => {
    subReport.reportSections?.forEach(section =>
      result.push({ subReportName: subReport.dName, section })
    );
  });
  return result;
};

// ── Core renderer ────────────────────────────────────────────────────────────

/**
 * Execute a single AdminReportSectionDto's reportScript and return the HTML
 * output string.
 *
 * The script runs in a sandboxed context that exposes:
 *   getData(mvarName)        — answer value from responseData
 *   getPHI(field)            — PHI value (dname, fname, lname, dob, gender, email, or extra[])
 *   getPreload(name)         — reportSectionPreload value
 *   getAdmin(varName)        — alias for getData
 *   adminResponse            — full AdminResponseDto
 *   formatDate(dateStr)      — locale-aware date formatter
 *   calculateScore(varNames) — sum of numeric answer values
 *   log(msg)                 — console.log wrapper
 *   Math, parseInt, parseFloat, String, Number, Boolean
 *
 * The script appends to the local variable `obuf` and the function returns it.
 * On execution error the function returns an inline error paragraph (never throws).
 */
export const generateReportSectionHtml = (
  reportSection: AdminReportSectionDto,
  responseData: AdminResponseDto,
  phi?: AppProfilePHIDto
): string => {
  try {
    // Build data lookup maps
    const dataContext = new Map<string, string>();
    responseData.responseData.forEach(item => {
      dataContext.set(item.name, item.value);
    });

    const preloadContext = new Map<string, string>();
    reportSection.reportSectionPreloads?.forEach(preload => {
      preloadContext.set(preload.name, preload.value);
    });

    const getPreload = (name: string): string => preloadContext.get(name) ?? '';

    const getPHI = (field: string): string => {
      if (!phi) return '';
      const key = field.toLowerCase();
      const direct: Record<string, string> = {
        dname:  phi.dname,
        fname:  phi.fname,
        lname:  phi.lname,
        email:  phi.email,
        dob:    phi.dob,
        gender: phi.gender,
      };
      if (key in direct) return direct[key];
      return phi.extra.find(e => e.name.toLowerCase() === key)?.value ?? '';
    };

    const scriptContext = {
      data:         dataContext,
      responseData: responseData.responseData,
      adminResponse: responseData,
      phiData:      phi ?? null,
      getPHI,
      preloadData:  preloadContext,
      getPreload,
      getAdmin: (varName: string) => dataContext.get(varName) || '',
      getData:  (varName: string) => {
        const entry = responseData.responseData.find(r => r.name === varName);
        return entry?.value || '';
      },
      formatDate: (dateStr: string) => {
        try {
          return new Date(dateStr).toLocaleDateString('en-US', {
            year: 'numeric', month: 'long', day: 'numeric',
          });
        } catch {
          return dateStr;
        }
      },
      calculateScore: (varNames: string[]) =>
        varNames.reduce((sum, varName) => {
          const value = dataContext.get(varName);
          const numValue = parseInt(value || '0', 10);
          return sum + (isNaN(numValue) ? 0 : numValue);
        }, 0),
      log: (message: string) => console.log('📊 ReportScript:', message),
      Math,
      parseInt,
      parseFloat,
      String,
      Number,
      Boolean,
    };

    // eslint-disable-next-line no-new-func
    const scriptFunction = new Function('context', `
      const {
        data,
        responseData,
        adminResponse,
        phiData,
        getPHI,
        preloadData,
        getPreload,
        getData,
        getResponseValue,
        formatDate,
        calculateScore,
        log,
        Math,
        parseInt,
        parseFloat,
        String,
        Number,
        Boolean
      } = context;

      let obuf = '';

      ${reportSection.reportScript}

      return obuf;
    `);

    const result = scriptFunction(scriptContext);
    return result || '';
  } catch (error) {
    console.error('❌ Error generating report section:', error);
    return `<p style="color:#e74c3c;font-family:sans-serif">Error in report section "${
      reportSection.dName
    }": ${error instanceof Error ? error.message : String(error)}</p>`;
  }
};

/**
 * Run all sections in a report in order, concatenate their HTML output, and
 * return the full report HTML string.
 *
 * @param report          AdminAssessmentReportDto to render
 * @param responseData    AdminResponseDto containing answer data
 * @param phi             Optional PHI context for getPHI() calls in scripts
 * @param editedScripts   Optional map of reportSectionId → overridden script
 *                        (used when an editor has unsaved changes)
 */
export const generateFullReportHtml = (
  report: AdminAssessmentReportDto,
  responseData: AdminResponseDto,
  phi?: AppProfilePHIDto,
  editedScripts?: Record<string, string>
): string => {
  const sections = getAllReportSections(report);
  const parts: string[] = [];
  for (const section of sections) {
    const effectiveSection =
      editedScripts?.[section.reportSectionId] !== undefined
        ? { ...section, reportScript: editedScripts[section.reportSectionId] }
        : section;
    const html = generateReportSectionHtml(effectiveSection, responseData, phi);
    if (html) parts.push(html);
  }
  return parts.join('\n') || '<p>No output returned from report scripts.</p>';
};

// ── WebView wrapper ──────────────────────────────────────────────────────────

/**
 * Wrap a raw HTML output string in a minimal full HTML document suitable for
 * loading in a WebView (or an iframe on web).
 */
export const wrapReportHtml = (html: string): string => `<!DOCTYPE html>
<html>
<head>
<meta name="viewport" content="width=device-width, initial-scale=1">
<style>
  body { margin: 0; padding: 8px; font-family: -apple-system, BlinkMacSystemFont, sans-serif;
         font-size: 14px; color: #2c3e50; line-height: 1.5; background: transparent; }
  * { box-sizing: border-box; }
</style>
</head>
<body>${html}</body>
</html>`;
